import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import CreatableSelect from 'react-select/creatable';
import matchSorter from 'match-sorter';
import { v4 as uuidv4 } from 'uuid';
import {
  Button,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from 'reactstrap';
import { useIntl } from 'react-intl';

import {
  removePersonFromPeopleList,
  updatePeopleOnPeopleList,
} from '../actions/People';

import {
  APIgetPeople,
  APImergePerson,
  APIupdateFace,
} from '../api/Faces';

import PersonSelectItem from './ModalPersonSelectItem';

import { sendTrackingEvent } from '../helpers';
import { eventDispatch } from '../helpers/Events';
import { userHasFeature } from '../helpers/Features';

import ErrorBoundary from './ErrorBoundary';
import ProcessingSpinner from './ProcessingSpinner';

const ChangePersonModal = ({
  dispatch,
  handleClose,
  face,
  person,
  onlySimilar,
}) => {
  const [editedFace, setEditedFace] = useState([]);
  const [editedPerson, setEditedPerson] = useState(null);
  const [inputValue, setInputValue] = useState(null);
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [creatingPerson, setCreatingPerson] = useState(false);

  const intl = useIntl();

  const similarPepopleIds = (face && face.person && face.person.similar) ?
    face.person.similar.map(sp => (sp.uuid)).concat([face?.person.uuid]) : [];

  useEffect(() => {
    sendTrackingEvent('change-person-modal-opened');
  }, []);

  useEffect(() => {
    const fetchPeople = async () => {
      setLoading(true);
      const response = await APIgetPeople({ all: true });
      setPeople(response);
      setLoading(false);
    };
    setEditedFace(face);
    setEditedPerson(person);
    fetchPeople();
  }, [face, person]);

  const handleSave = async () => {
    setSaving(true);
    let response;
    if (face) {
      response = await APIupdateFace({
        ...editedFace,
        person_uuid: editedFace.person?.uuid || null,
      });
      setEditedFace(response);
      eventDispatch('faceUpdated', response);
    }

    if (person) {
      response = await APImergePerson(person.uuid, editedPerson.uuid);
      setEditedPerson(response);
      dispatch(removePersonFromPeopleList(person.uuid));
      dispatch(updatePeopleOnPeopleList([response]));

      sendTrackingEvent('change-person-saved');
    }

    setSaving(false);
    handleClose();
  };

  const handleCreatePerson = async (name) => {
    setCreatingPerson(true);
    const personMockup = {
      uuid: uuidv4(),
      name,
    };
    setPeople(people.concat(personMockup));
    setEditedFace({ ...editedFace, person: personMockup });
    eventDispatch('faceUpdated', personMockup);
    setCreatingPerson(false);
    setSaving(false);

    sendTrackingEvent('change-person-created');
  };

  const getPersonOptions = () => {
    const options = [];
    people.forEach(p => {
      if (!onlySimilar || similarPepopleIds.includes(p.uuid)) {
        options.push({
          value: p.uuid,
          name: p.name || intl.formatMessage({ defaultMessage: 'Unnamed person' }),
          label: p.name || intl.formatMessage({ defaultMessage: 'Unnamed person' }),
          avatar: p.image_path,
          images: p.faces || 0,
        });
      }
    });
    return options;
  };

  const handleChange = (values, { action }) => {
    if (face) {
      if (action === 'clear') {
        setEditedFace({ ...editedFace, person: null });
      } else {
        const nPerson = people.find(p => (p.uuid === values.value));
        if (nPerson) {
          setEditedFace({ ...editedFace, person: nPerson });
        }
      }
    }

    if (person) {
      if (action === 'clear') {
        setEditedPerson(null);
      } else {
        const nPerson = people.find(p => (p.uuid === values.value));
        if (nPerson) {
          setEditedPerson(nPerson);
        }
      }
    }
  };

  const getSelected = () => (
    getPersonOptions().find(
      p => (p.value === editedFace?.person?.uuid || p.value === editedPerson?.uuid),
    )
  );

  return (
    <ErrorBoundary>
      <ModalHeader toggle={handleClose}>
        {person ? (
          intl.formatMessage({ defaultMessage: 'Merge people' })
        ) : (
          intl.formatMessage({ defaultMessage: 'Select or change person' })
        )}
      </ModalHeader>
      <ModalBody style={{ overflow: 'visible' }}>
        <div className="file-faces-element">
          {onlySimilar && (
            <p>
              {intl.formatMessage({ defaultMessage: 'Showing people with the same name as {name}' }, { name: face.person.name })}
            </p>
          )}
          {person && (
            <p className="error">
              {intl.formatMessage({ defaultMessage: 'You are about to merge the selected person with your selection below.' })}
            </p>
          )}
          <div className="editable-field-person-wrapper">
            <CreatableSelect
              id={face ? `face-${face.uuid}` : `person-${person.uuid}`}
              className="user-select"
              classNamePrefix="user-select"
              name="facePerson"
              autoFocus
              components={{
                Option: PersonSelectItem,
              }}
              isDisabled={saving || creatingPerson || loading || !userHasFeature('face-recognition')}
              isLoading={saving || creatingPerson || loading}
              isClearable={true}
              placeholder={intl.formatMessage({ defaultMessage: 'Search or create' })}
              options={matchSorter(getPersonOptions(), inputValue, { keys: ['label'] })}
              filterOption={false}
              onInputChange={(v) => { setInputValue(v); }}
              formatCreateLabel={name => (intl.formatMessage({ defaultMessage: 'Create: {name}' }, { name }))}
              onChange={handleChange}
              onCreateOption={handleCreatePerson}
              value={getSelected()}
              menuPortalTarget={document.body}
              styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
            />
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <div className="buttons-group">
          <Button
            color="secondary"
            disabled={saving}
            onClick={handleClose}
          >
            {intl.formatMessage({ defaultMessage: 'Cancel' })}
          </Button>
          <Button
            color="primary"
            disabled={saving}
            onClick={handleSave}
          >
            <ProcessingSpinner show={saving} />
            {saving ? (
              intl.formatMessage({ defaultMessage: 'Saving' })
            ) : (
              intl.formatMessage({ defaultMessage: 'Save' })
            )}
          </Button>
        </div>
      </ModalFooter>
    </ErrorBoundary>
  );
};

ChangePersonModal.propTypes = {
  dispatch: PropTypes.func.isRequired,
  handleClose: PropTypes.func.isRequired,
  face: PropTypes.object,
  person: PropTypes.object,
  onlySimilar: PropTypes.bool,
};

ChangePersonModal.defaultProps = {
  face: null,
  person: null,
  onlySimilar: false,
};

export default connect()(ChangePersonModal);
